import React, { useEffect, useRef, useCallback } from 'react';
import { useCall } from './CallProvider';

const JITSI_DOMAIN = 'meet.jit.si';
const JITSI_SCRIPT = `https://${JITSI_DOMAIN}/external_api.js`;

let scriptLoaded = false;
let scriptLoading = false;
const loadCallbacks = [];

function loadJitsiScript() {
  if (scriptLoaded) return Promise.resolve();
  if (scriptLoading) return new Promise(r => loadCallbacks.push(r));

  scriptLoading = true;
  return new Promise((resolve, reject) => {
    loadCallbacks.push(resolve);
    const tag = document.createElement('script');
    tag.src = JITSI_SCRIPT;
    tag.async = true;
    tag.onload = () => {
      scriptLoaded = true;
      scriptLoading = false;
      loadCallbacks.forEach(cb => cb());
      loadCallbacks.length = 0;
    };
    tag.onerror = () => {
      scriptLoading = false;
      reject(new Error('Failed to load Jitsi script'));
    };
    document.head.appendChild(tag);
  });
}

export default function JitsiEmbed({ onRecordingStream }) {
  const { callInfo, callState, isMuted, isVideoOn, endCall, CALL_STATES } = useCall();
  const containerRef = useRef(null);
  const apiRef = useRef(null);

  const disposeJitsi = useCallback(() => {
    if (apiRef.current) {
      try { apiRef.current.dispose(); } catch (_) {}
      apiRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (callState !== CALL_STATES.ACTIVE || !callInfo?.jitsiRoom) {
      disposeJitsi();
      return;
    }

    let cancelled = false;

    loadJitsiScript().then(() => {
      if (cancelled || !containerRef.current) return;
      if (!window.JitsiMeetExternalAPI) return;

      disposeJitsi();

      const isAudioOnly = callInfo.callType === 'audio';
      const api = new window.JitsiMeetExternalAPI(JITSI_DOMAIN, {
        roomName: callInfo.jitsiRoom,
        parentNode: containerRef.current,
        width: '100%',
        height: '100%',
        configOverwrite: {
          startWithAudioMuted: false,
          startWithVideoMuted: isAudioOnly,
          disableDeepLinking: true,
          prejoinPageEnabled: false,
          enableLobby: false,
          toolbarButtons: [
            'microphone', 'camera', 'hangup', 'fullscreen',
            'tileview', 'toggle-camera',
          ],
          enableNoisyMicDetection: false,
          disableRemoteMute: true,
          p2p: { enabled: true },
        },
        interfaceConfigOverwrite: {
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          SHOW_BRAND_WATERMARK: false,
          SHOW_POWERED_BY: false,
          TOOLBAR_ALWAYS_VISIBLE: false,
          FILM_STRIP_MAX_HEIGHT: 100,
          DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
        },
        userInfo: {
          displayName: callInfo.fromName || 'VTD User',
        },
      });

      apiRef.current = api;

      api.addEventListener('readyToClose', () => {
        endCall();
      });

      api.addEventListener('videoConferenceLeft', () => {
        endCall();
      });

      // Expose the audio stream for recording
      if (onRecordingStream) {
        api.addEventListener('videoConferenceJoined', () => {
          try {
            const iframe = containerRef.current?.querySelector('iframe');
            if (iframe) {
              onRecordingStream(iframe);
            }
          } catch (_) {}
        });
      }
    }).catch(err => {
      console.error('[Jitsi] Load failed:', err);
    });

    return () => {
      cancelled = true;
      disposeJitsi();
    };
  }, [callState, callInfo, endCall, disposeJitsi, onRecordingStream, CALL_STATES]);

  // Sync mute/video state to Jitsi
  useEffect(() => {
    if (!apiRef.current) return;
    apiRef.current.isAudioMuted().then(muted => {
      if (muted !== isMuted) apiRef.current.executeCommand('toggleAudio');
    }).catch(() => {});
  }, [isMuted]);

  useEffect(() => {
    if (!apiRef.current) return;
    apiRef.current.isVideoMuted().then(muted => {
      const shouldBeMuted = !isVideoOn;
      if (muted !== shouldBeMuted) apiRef.current.executeCommand('toggleVideo');
    }).catch(() => {});
  }, [isVideoOn]);

  if (callState !== CALL_STATES.ACTIVE || !callInfo?.jitsiRoom) return null;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 10,
        background: '#000',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    />
  );
}
